import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  RefreshCw,
  Eye,
  Trash2,
  AlertCircle,
  Loader2,
  X,
  CheckCircle,
  User,
  Mail as MailIcon,
  AlertTriangle,
  ArchiveIcon,
  TrendingUp,
  Calendar,
  Clock,
  Settings,
  PlusCircle,
  Globe as GlobeIcon,
  Share2 as Share2Icon,
  Link as LinkIcon,
  Save as SaveIcon,
  Image as ImageIcon,
  Edit2,
  FileText,
  Star,
  Zap,
  ThumbsUp as ThumbsUpIcon2,
  MessageSquare as MessageSquareIcon2,
  Tag,
  AlertOctagon,
  MessageCircle,
  BarChart3,
  Globe,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Upload as UploadIcon2,
  Image as ImageIcon2,
  X as XIcon,
  Plus,
  Trash2 as TrashIcon,
} from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { blogService } from "../../api/services/blogService";
import { format, formatDistance } from "date-fns";
import { useToast } from "../../components/common/ThemeToaster";

// Helper Functions
const getStatusInfo = (status) => {
  const statuses = {
    draft: {
      icon: FileText,
      label: "Draft",
      color: "text-gray-500",
      bg: "bg-gray-100 dark:bg-gray-800",
      border: "border-gray-200 dark:border-gray-700",
    },
    published: {
      icon: CheckCircle,
      label: "Published",
      color: "text-green-500",
      bg: "bg-green-50 dark:bg-green-900/20",
      border: "border-green-200 dark:border-green-800",
    },
    scheduled: {
      icon: Clock,
      label: "Scheduled",
      color: "text-yellow-500",
      bg: "bg-yellow-50 dark:bg-yellow-900/20",
      border: "border-yellow-200 dark:border-yellow-800",
    },
    archived: {
      icon: ArchiveIcon,
      label: "Archived",
      color: "text-gray-500",
      bg: "bg-gray-100 dark:bg-gray-800",
      border: "border-gray-200 dark:border-gray-700",
    },
  };
  return statuses[status?.toLowerCase()] || statuses.draft;
};

const getCommentStatusInfo = (status) => {
  const statuses = {
    pending: {
      icon: Clock,
      label: "Pending",
      color: "text-yellow-500",
      bg: "bg-yellow-50 dark:bg-yellow-900/20",
      border: "border-yellow-200 dark:border-yellow-800",
    },
    approved: {
      icon: CheckCircle,
      label: "Approved",
      color: "text-green-500",
      bg: "bg-green-50 dark:bg-green-900/20",
      border: "border-green-200 dark:border-green-800",
    },
    spam: {
      icon: AlertOctagon,
      label: "Spam",
      color: "text-red-500",
      bg: "bg-red-50 dark:bg-red-900/20",
      border: "border-red-200 dark:border-red-800",
    },
    trashed: {
      icon: Trash2,
      label: "Trashed",
      color: "text-gray-500",
      bg: "bg-gray-100 dark:bg-gray-800",
      border: "border-gray-200 dark:border-gray-700",
    },
  };
  return statuses[status?.toLowerCase()] || statuses.pending;
};

const formatTime = (date) => {
  if (!date) return "N/A";
  return formatDistance(new Date(date), new Date(), { addSuffix: true });
};

// Rich Text Editor Toolbar Configuration
const modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["blockquote", "code-block"],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "bullet",
  "check",
  "indent",
  "align",
  "blockquote",
  "code-block",
  "link",
  "image",
  "video",
];

// Generate slug from title
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, trend }) => (
  <motion.div
    whileHover={{ y: -2, scale: 1.02 }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
    className="relative overflow-hidden bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-white/50 dark:border-gray-800/50 shadow-lg"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 opacity-5" />
    <div className="relative p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-md">
          <Icon size={20} className="text-white" />
        </div>
        <span className="text-2xl font-bold text-gray-900 dark:text-white">
          {value?.toLocaleString() || 0}
        </span>
      </div>
      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
        {label}
      </p>
      {trend && (
        <div className="flex items-center gap-1 mt-2 text-xs">
          <TrendingUp size={12} className="text-emerald-500" />
          <span className="text-emerald-600 dark:text-emerald-400">
            +{trend}%
          </span>
          <span className="text-gray-500">vs last week</span>
        </div>
      )}
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 " />
  </motion.div>
);

// Blog Card Component
const BlogCard = ({
  blog,
  isSelected,
  onSelect,
  onViewDetails,
  onEdit,
  onPublish,
  onArchive,
  onDelete,
  onToggleFeatured,
  onManageComments,
}) => {
  const status = getStatusInfo(blog.status);
  const StatusIcon = status.icon;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ y: -2 }}
      transition={{ type: "spring", stiffness: 300 }}
      className={`group bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border ${
        blog.status === "draft"
          ? "border-primary-200 dark:border-primary-800 bg-gradient-to-r from-primary-50/30 to-white dark:from-primary-900/10 dark:to-gray-900"
          : "border-gray-200/50 dark:border-gray-800/50"
      } shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden`}
    >
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 pt-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onSelect(blog.id)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
          </div>
          {blog.featuredImage && (
            <div className="flex-shrink-0">
              <img
                src={blog.featuredImage}
                alt={blog.title}
                className="w-20 h-20 object-cover rounded-lg"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${status.bg} ${status.color} border ${status.border}`}
                  >
                    <StatusIcon size={10} /> {status.label}
                  </span>
                  {blog.isFeatured && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800">
                      <Star size={10} /> Featured
                    </span>
                  )}
                  {blog.isPopular && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 border border-purple-200 dark:border-purple-800">
                      <ThumbsUpIcon2 size={10} /> Popular
                    </span>
                  )}
                  {blog.isTrending && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400 border border-orange-200 dark:border-orange-800">
                      <TrendingUp size={10} /> Trending
                    </span>
                  )}
                  {blog.category && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                      <FolderOpen size={10} /> {blog.category.name}
                    </span>
                  )}
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                  {blog.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                  {blog.excerpt ||
                    blog.content?.substring(0, 150)?.replace(/<[^>]*>/g, "")}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <User size={12} /> {blog.author || "Unknown"}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar size={12} />{" "}
                    {blog.publishedAt
                      ? formatTime(blog.publishedAt)
                      : formatTime(blog.createdAt)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye size={12} /> {blog.views || 0} views
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageCircle size={12} /> {blog.commentsCount || 0}{" "}
                    comments
                  </span>
                  {blog.pendingCommentsCount > 0 && (
                    <span className="flex items-center gap-1 text-yellow-500">
                      <Clock size={12} /> {blog.pendingCommentsCount} pending
                    </span>
                  )}
                </div>
                {blog.tags && blog.tags.length > 0 && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                    <Tag size={12} />{" "}
                    <span>{blog.tags.slice(0, 3).join(", ")}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => onViewDetails(blog)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="View Details"
                >
                  <Eye size={14} className="text-blue-500" />
                </button>
                <button
                  onClick={() => onManageComments(blog)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="Manage Comments"
                >
                  <MessageCircle size={14} className="text-purple-500" />
                </button>
                <button
                  onClick={() => onEdit(blog)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="Edit"
                >
                  <Edit2 size={14} className="text-green-500" />
                </button>
                {blog.status !== "published" && (
                  <button
                    onClick={() => onPublish(blog.id)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    title="Publish"
                  >
                    <CheckCircle size={14} className="text-emerald-500" />
                  </button>
                )}
                <button
                  onClick={() => onToggleFeatured(blog.id, !blog.isFeatured)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title={
                    blog.isFeatured ? "Remove Featured" : "Mark as Featured"
                  }
                >
                  {blog.isFeatured ? (
                    <Star size={14} className="text-yellow-500" />
                  ) : (
                    <Star size={14} className="text-gray-400" />
                  )}
                </button>
                <button
                  onClick={() => onArchive(blog.id)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="Archive"
                >
                  <ArchiveIcon size={14} className="text-gray-500" />
                </button>
                <button
                  onClick={() => onDelete(blog)}
                  className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  title="Delete"
                >
                  <Trash2 size={14} className="text-red-500" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Create/Edit Blog Modal with Rich Text Editor, Category Selection, and Image Upload
const CreateEditBlogModal = ({ blog, onClose, onSuccess }) => {
  const isEditing = !!blog?.id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("content");
  const [content, setContent] = useState("");
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [featuredImagePreview, setFeaturedImagePreview] = useState("");
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const { success: toastSuccess, error: toastError } = useToast();

  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    status: "draft",
    categoryId: "",
    tags: [],
    isFeatured: false,
    isPopular: false,
    isTrending: false,
    allowComments: true,
    scheduledAt: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
  });

  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (blog && isEditing) {
      setFormData({
        title: blog.title || "",
        excerpt: blog.excerpt || "",
        status: (blog.status || "draft").toLowerCase(),
        categoryId: blog.categoryId || blog.category?.id || "",
        tags: blog.tags || [],
        isFeatured: blog.isFeatured || false,
        isPopular: blog.isPopular || false,
        isTrending: blog.isTrending || false,
        allowComments: blog.allowComments !== false,
        scheduledAt: blog.scheduledAt || "",
        metaTitle: blog.metaTitle || "",
        metaDescription: blog.metaDescription || "",
        metaKeywords: blog.metaKeywords || "",
      });
      setContent(blog.content || "");

      // Handle featured image - check for both featuredImage URL and featuredImageBlob
      if (blog.featuredImage) {
        // If backend already converted to base64 data URL
        setFeaturedImagePreview(blog.featuredImage);
      } else if (blog.featuredImageBlob && blog.featuredImageMimeType) {
        // Convert Buffer to base64 data URL
        const base64 = convertBufferToBase64(blog.featuredImageBlob);
        if (base64) {
          setFeaturedImagePreview(
            `data:${blog.featuredImageMimeType};base64,${base64}`,
          );
        }
      }

      if (blog.galleryImages && blog.galleryImages.length > 0) {
        setGalleryPreviews(blog.galleryImages);
      }
      setTagInput("");
    }
  }, [blog, isEditing]);

  // Helper function to convert Buffer to base64
  const convertBufferToBase64 = (buffer) => {
    if (!buffer) return null;

    // If buffer has data array (from JSON serialization)
    if (buffer.data && Array.isArray(buffer.data)) {
      const bytes = new Uint8Array(buffer.data);
      let binary = "";
      const chunkSize = 8192;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(
          null,
          bytes.subarray(i, i + chunkSize),
        );
      }
      return btoa(binary);
    }

    // If buffer is already a base64 string
    if (typeof buffer === "string") {
      return buffer;
    }

    return null;
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await blogService.getBlogCategories();
      let categoriesData = [];
      if (response?.data?.data?.categories) {
        categoriesData = response.data.data.categories;
      } else if (response?.data?.categories) {
        categoriesData = response.data.categories;
      } else if (response?.categories) {
        categoriesData = response.categories;
      } else if (Array.isArray(response?.data)) {
        categoriesData = response.data;
      } else if (Array.isArray(response)) {
        categoriesData = response;
      }
      setCategories(categoriesData);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleFeaturedImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFeaturedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFeaturedImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryImagesChange = (e) => {
    const files = Array.from(e.target.files);
    setGalleryImages((prev) => [...prev, ...files]);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setGalleryPreviews((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeGalleryImage = (index) => {
    setGalleryImages((prev) => prev.filter((_, i) => i !== index));
    setGalleryPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setError("Title is required");
      toastError("Title is required");
      return;
    }
    if (!content.trim()) {
      setError("Content is required");
      toastError("Content is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const blogData = {
        title: formData.title.trim(),
        content: content,
        excerpt: formData.excerpt || undefined,
        status: formData.status.toLowerCase(),
        categoryId: formData.categoryId || undefined,
        tags: formData.tags.length > 0 ? formData.tags : [],
        isFeatured: formData.isFeatured,
        isPopular: formData.isPopular,
        isTrending: formData.isTrending,
        allowComments: formData.allowComments,
        scheduledAt: formData.scheduledAt || undefined,
        metaTitle: formData.metaTitle || undefined,
        metaDescription: formData.metaDescription || undefined,
        metaKeywords: formData.metaKeywords || undefined,
      };

      if (isEditing) {
        await blogService.updateBlog(
          blog.id,
          blogData,
          featuredImage,
          galleryImages,
        );
        toastSuccess("Blog updated successfully!");
      } else {
        await blogService.createBlog(blogData, featuredImage, galleryImages);
        toastSuccess("Blog created successfully!");
      }

      onSuccess();
    } catch (err) {
      console.error("Submit error:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        `Failed to ${isEditing ? "update" : "create"} blog`;
      setError(errorMessage);
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: "content", label: "Content", icon: FileText },
    { id: "images", label: "Images", icon: ImageIcon },
    { id: "settings", label: "Settings", icon: Settings },
    { id: "seo", label: "SEO", icon: Globe },
  ];

  const renderContent = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="Enter blog title"
          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Slug will be auto-generated from title
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Category
        </label>
        <select
          name="categoryId"
          value={formData.categoryId}
          onChange={handleChange}
          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={loadingCategories}
        >
          <option value="">Select a category (optional)</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}{" "}
              {category.postsCount ? `(${category.postsCount} posts)` : ""}
            </option>
          ))}
        </select>
        {loadingCategories && (
          <p className="text-xs text-gray-500 mt-1">Loading categories...</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Excerpt
        </label>
        <textarea
          name="excerpt"
          value={formData.excerpt}
          onChange={handleChange}
          rows={3}
          placeholder="Brief summary of the blog post"
          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all resize-none"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Content <span className="text-red-500">*</span>
        </label>
        <ReactQuill
          theme="snow"
          value={content}
          onChange={setContent}
          modules={modules}
          formats={formats}
          className="bg-white dark:bg-gray-800 rounded-xl"
          placeholder="Write your blog content here..."
        />
      </div>
    </div>
  );

  const renderImages = () => (
    <div className="space-y-6">
      {/* Featured Image */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Featured Image
        </label>
        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-xl hover:border-primary-500 transition-colors">
          <div className="space-y-1 text-center">
            {featuredImagePreview ? (
              <div className="relative">
                <img
                  src={featuredImagePreview}
                  alt="Featured preview"
                  className="mx-auto h-48 w-auto object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => {
                    setFeaturedImage(null);
                    setFeaturedImagePreview("");
                  }}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                >
                  <XIcon size={16} />
                </button>
              </div>
            ) : (
              <>
                <UploadIcon2 className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600 dark:text-gray-400">
                  <label
                    htmlFor="featured-image"
                    className="relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none"
                  >
                    <span>Upload a file</span>
                    <input
                      id="featured-image"
                      name="featured-image"
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onChange={handleFeaturedImageChange}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF up to 10MB
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Gallery Images */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Gallery Images
        </label>
        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-xl hover:border-primary-500 transition-colors">
          <div className="space-y-1 text-center">
            <UploadIcon2 className="mx-auto h-12 w-12 text-gray-400" />
            <div className="flex text-sm text-gray-600 dark:text-gray-400">
              <label
                htmlFor="gallery-images"
                className="relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none"
              >
                <span>Upload multiple files</span>
                <input
                  id="gallery-images"
                  name="gallery-images"
                  type="file"
                  className="sr-only"
                  multiple
                  accept="image/*"
                  onChange={handleGalleryImagesChange}
                />
              </label>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs text-gray-500">
              PNG, JPG, GIF up to 10MB each
            </p>
          </div>
        </div>
        {galleryPreviews.length > 0 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {galleryPreviews.map((preview, index) => (
              <div key={index} className="relative group">
                <img
                  src={preview}
                  alt={`Gallery ${index + 1}`}
                  className="h-24 w-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removeGalleryImage(index)}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <TrashIcon size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Status
        </label>
        <select
          name="status"
          value={formData.status}
          onChange={handleChange}
          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="scheduled">Scheduled</option>
        </select>
      </div>

      {formData.status === "scheduled" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Schedule Date & Time
          </label>
          <input
            type="datetime-local"
            name="scheduledAt"
            value={formData.scheduledAt}
            onChange={handleChange}
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Tags
        </label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleAddTag()}
            placeholder="Add a tag..."
            className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button
            type="button"
            onClick={handleAddTag}
            className="px-4 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium hover:opacity-90 transition-all"
          >
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.tags.map((tag, index) => (
            <div
              key={index}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full"
            >
              <Tag size={12} className="text-gray-500" />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {tag}
              </span>
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                className="p-0.5 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X size={12} className="text-gray-500" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Flags
        </h3>
        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Featured Post
            </span>
            <p className="text-xs text-gray-500">
              Display on homepage and featured sections
            </p>
          </div>
          <button
            type="button"
            onClick={() =>
              setFormData((prev) => ({ ...prev, isFeatured: !prev.isFeatured }))
            }
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ${
              formData.isFeatured
                ? "bg-primary-600"
                : "bg-gray-200 dark:bg-gray-700"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                formData.isFeatured ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </label>

        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Popular Post
            </span>
            <p className="text-xs text-gray-500">Mark as popular content</p>
          </div>
          <button
            type="button"
            onClick={() =>
              setFormData((prev) => ({ ...prev, isPopular: !prev.isPopular }))
            }
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ${
              formData.isPopular
                ? "bg-primary-600"
                : "bg-gray-200 dark:bg-gray-700"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                formData.isPopular ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </label>

        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Trending Post
            </span>
            <p className="text-xs text-gray-500">Mark as trending content</p>
          </div>
          <button
            type="button"
            onClick={() =>
              setFormData((prev) => ({ ...prev, isTrending: !prev.isTrending }))
            }
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ${
              formData.isTrending
                ? "bg-primary-600"
                : "bg-gray-200 dark:bg-gray-700"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                formData.isTrending ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </label>

        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Allow Comments
            </span>
            <p className="text-xs text-gray-500">
              Enable commenting on this post
            </p>
          </div>
          <button
            type="button"
            onClick={() =>
              setFormData((prev) => ({
                ...prev,
                allowComments: !prev.allowComments,
              }))
            }
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ${
              formData.allowComments
                ? "bg-primary-600"
                : "bg-gray-200 dark:bg-gray-700"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
                formData.allowComments ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </label>
      </div>
    </div>
  );

  const renderSEO = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Meta Title
        </label>
        <input
          type="text"
          name="metaTitle"
          value={formData.metaTitle}
          onChange={handleChange}
          placeholder="SEO title (defaults to blog title)"
          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <p className="text-xs text-gray-500 mt-1">
          Recommended length: 50-60 characters
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Meta Description
        </label>
        <textarea
          name="metaDescription"
          value={formData.metaDescription}
          onChange={handleChange}
          rows={3}
          placeholder="Brief description for search engines"
          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
        />
        <p className="text-xs text-gray-500 mt-1">
          Recommended length: 150-160 characters
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Meta Keywords
        </label>
        <input
          type="text"
          name="metaKeywords"
          value={formData.metaKeywords}
          onChange={handleChange}
          placeholder="keyword1, keyword2, keyword3"
          className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <p className="text-xs text-gray-500 mt-1">Comma-separated keywords</p>
      </div>
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case "content":
        return renderContent();
      case "images":
        return renderImages();
      case "settings":
        return renderSettings();
      case "seo":
        return renderSEO();
      default:
        return renderContent();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 p-5 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {isEditing ? "Edit Blog" : "Create New Blog"}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {isEditing ? "Update your blog content" : "Write a new blog post"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex gap-1 border-b border-gray-100 dark:border-gray-800 px-5 pt-2 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-xl transition-all whitespace-nowrap ${
                  isActive
                    ? "bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400 border-b-2 border-primary-500"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center gap-3 text-red-600 text-sm">
              <AlertCircle size={18} />
              {error}
            </div>
          )}
          {renderActiveTab()}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 p-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium disabled:opacity-50 transition-all hover:opacity-90"
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <SaveIcon size={18} />
            )}
            {loading ? "Saving..." : isEditing ? "Update Blog" : "Create Blog"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Comments Moderation Modal
const CommentsModerationModal = ({ blog, onClose }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const { success: toastSuccess, error: toastError } = useToast();

  useEffect(() => {
    fetchComments();
  }, [blog, filterStatus]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const params = {
        limit: 100,
      };
      // Send status in lowercase as API expects
      if (filterStatus !== "all") {
        params.status = filterStatus.toLowerCase();
      }
      const response = await blogService.getBlogComments(blog.id, params);

      let commentsData = [];
      if (response?.data?.data?.comments) {
        commentsData = response.data.data.comments;
      } else if (response?.data?.comments) {
        commentsData = response.data.comments;
      } else if (response?.comments) {
        commentsData = response.comments;
      }

      // Keep status as is from API (lowercase)
      const normalizedComments = commentsData.map((comment) => ({
        ...comment,
        replies: comment.replies || [],
      }));

      normalizedComments.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
      setComments(normalizedComments);
      setError("");
    } catch (err) {
      console.error("Fetch comments error:", err);
      setError("Failed to load comments");
      toastError("Failed to load comments");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to recursively update comment status
  const updateCommentStatusRecursive = (commentsList, commentId, newStatus) => {
    return commentsList.map((comment) => {
      if (comment.id === commentId) {
        return { ...comment, status: newStatus };
      }
      if (comment.replies && comment.replies.length > 0) {
        return {
          ...comment,
          replies: updateCommentStatusRecursive(
            comment.replies,
            commentId,
            newStatus,
          ),
        };
      }
      return comment;
    });
  };

  // Helper function to recursively delete comment
  const deleteCommentRecursive = (commentsList, commentId) => {
    return commentsList.filter((comment) => {
      if (comment.id === commentId) {
        return false;
      }
      if (comment.replies && comment.replies.length > 0) {
        comment.replies = deleteCommentRecursive(comment.replies, commentId);
      }
      return true;
    });
  };

  const handleApproveComment = async (commentId) => {
    setActionLoading(true);
    try {
      await blogService.approveComment(commentId);
      setComments((prevComments) =>
        updateCommentStatusRecursive(prevComments, commentId, "approved"),
      );
      toastSuccess("Comment approved successfully");
    } catch (err) {
      console.error("Approve error:", err);
      toastError(err.response?.data?.message || "Failed to approve comment");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectComment = async (commentId) => {
    setActionLoading(true);
    try {
      await blogService.rejectComment(commentId);
      setComments((prevComments) =>
        updateCommentStatusRecursive(prevComments, commentId, "spam"),
      );
      toastSuccess("Comment marked as spam");
    } catch (err) {
      console.error("Reject error:", err);
      toastError(err.response?.data?.message || "Failed to reject comment");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment? This action cannot be undone."))
      return;
    setActionLoading(true);
    try {
      await blogService.deleteComment(commentId);
      setComments((prevComments) =>
        deleteCommentRecursive(prevComments, commentId),
      );
      toastSuccess("Comment deleted successfully");
    } catch (err) {
      console.error("Delete error:", err);
      toastError(err.response?.data?.message || "Failed to delete comment");
    } finally {
      setActionLoading(false);
    }
  };

  const toggleReplies = (commentId) => {
    setExpandedComments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = status?.toLowerCase() || "pending";
    const statusInfo = getCommentStatusInfo(normalizedStatus);
    const StatusIcon = statusInfo.icon;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${statusInfo.bg} ${statusInfo.color} border ${statusInfo.border}`}
      >
        <StatusIcon size={10} /> {statusInfo.label}
      </span>
    );
  };

  const getCommentStatusInfo = (status) => {
    switch (status) {
      case "approved":
        return {
          label: "Approved",
          icon: CheckCircle,
          color: "text-green-700 dark:text-green-400",
          bg: "bg-green-100 dark:bg-green-900/20",
          border: "border-green-200 dark:border-green-800",
        };
      case "pending":
        return {
          label: "Pending",
          icon: Clock,
          color: "text-yellow-700 dark:text-yellow-400",
          bg: "bg-yellow-100 dark:bg-yellow-900/20",
          border: "border-yellow-200 dark:border-yellow-800",
        };
      case "spam":
        return {
          label: "Spam",
          icon: AlertOctagon,
          color: "text-red-700 dark:text-red-400",
          bg: "bg-red-100 dark:bg-red-900/20",
          border: "border-red-200 dark:border-red-800",
        };
      default:
        return {
          label: status || "Pending",
          icon: Clock,
          color: "text-yellow-700 dark:text-yellow-400",
          bg: "bg-yellow-100 dark:bg-yellow-900/20",
          border: "border-yellow-200 dark:border-yellow-800",
        };
    }
  };

  const formatTime = (date) => {
    if (!date) return "Unknown";
    const now = new Date();
    const commentDate = new Date(date);
    const diffMs = now - commentDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return commentDate.toLocaleDateString();
  };

  const filteredComments = comments.filter((comment) => {
    if (searchTerm) {
      return (
        comment.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comment.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comment.userEmail?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true;
  });

  // Calculate stats dynamically from current comments state
  const calculateStats = (commentsList) => {
    let stats = {
      total: commentsList.length,
      pending: 0,
      approved: 0,
      spam: 0,
    };

    const processComment = (comment) => {
      const status = comment.status?.toLowerCase() || "pending";
      switch (status) {
        case "pending":
          stats.pending++;
          break;
        case "approved":
          stats.approved++;
          break;
        case "spam":
          stats.spam++;
          break;
        default:
          stats.pending++;
          break;
      }
      if (comment.replies && comment.replies.length) {
        comment.replies.forEach(processComment);
      }
    };

    commentsList.forEach(processComment);
    return stats;
  };

  const stats = calculateStats(comments);

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-8">
          <Loader2
            size={40}
            className="animate-spin text-primary-500 mx-auto"
          />
          <p className="text-gray-500 mt-4">Loading comments...</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 p-5 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Manage Comments
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {blog.title} - {stats.total} comments total
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Stats Bar */}
        <div className="bg-gray-50/50 dark:bg-gray-800/50 p-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setFilterStatus("all")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg shadow-sm transition-all ${
                filterStatus === "all"
                  ? "bg-primary-500 text-white"
                  : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              <MessageCircle size={14} />
              <span className="text-sm font-medium">All: {stats.total}</span>
            </button>
            <button
              onClick={() => setFilterStatus("pending")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg shadow-sm transition-all ${
                filterStatus === "pending"
                  ? "bg-yellow-500 text-white"
                  : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              <Clock size={14} />
              <span className="text-sm font-medium">
                Pending: {stats.pending}
              </span>
            </button>
            <button
              onClick={() => setFilterStatus("approved")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg shadow-sm transition-all ${
                filterStatus === "approved"
                  ? "bg-green-500 text-white"
                  : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              <CheckCircle size={14} />
              <span className="text-sm font-medium">
                Approved: {stats.approved}
              </span>
            </button>
            <button
              onClick={() => setFilterStatus("spam")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg shadow-sm transition-all ${
                filterStatus === "spam"
                  ? "bg-red-500 text-white"
                  : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              <AlertOctagon size={14} />
              <span className="text-sm font-medium">Spam: {stats.spam}</span>
            </button>
          </div>
        </div>

        {/* Filter and Search Bar */}
        <div className="p-4 border-b border-gray-100 dark:border-gray-800 bg-white/50 dark:bg-gray-900/50">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search by name, email, or content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button
              onClick={fetchComments}
              className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center gap-3 text-red-600 text-sm">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          {filteredComments.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle size={48} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">No comments found</h3>
              <p className="text-gray-500">
                {searchTerm
                  ? "Try adjusting your search"
                  : "No comments in this category"}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredComments.map((comment) => {
                const commentStatus =
                  comment.status?.toLowerCase() || "pending";
                return (
                  <div
                    key={comment.id}
                    className="p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
                  >
                    {/* Comment Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-medium shadow-sm">
                          {comment.userName?.charAt(0).toUpperCase() || "U"}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-gray-900 dark:text-white">
                              {comment.userName || "Anonymous"}
                            </p>
                            {comment.userEmail && (
                              <span className="text-xs text-gray-400">
                                {comment.userEmail}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs text-gray-500">
                              {formatTime(comment.createdAt)}
                            </p>
                            {comment.editedAt && (
                              <span className="text-xs text-gray-400">
                                (edited)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(comment.status)}
                      </div>
                    </div>

                    {/* Comment Content */}
                    <p className="text-gray-700 dark:text-gray-300 text-sm mb-3 whitespace-pre-wrap">
                      {comment.content}
                    </p>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
                      {commentStatus === "pending" && (
                        <>
                          <button
                            onClick={() => handleApproveComment(comment.id)}
                            disabled={actionLoading}
                            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50"
                          >
                            <CheckCircle size={14} />
                            Approve
                          </button>
                          <button
                            onClick={() => handleRejectComment(comment.id)}
                            disabled={actionLoading}
                            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50"
                          >
                            <AlertOctagon size={14} />
                            Mark as Spam
                          </button>
                        </>
                      )}
                      {commentStatus === "approved" && (
                        <>
                          <button
                            onClick={() => handleRejectComment(comment.id)}
                            disabled={actionLoading}
                            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50"
                          >
                            <AlertOctagon size={14} />
                            Mark as Spam
                          </button>
                        </>
                      )}
                      {commentStatus === "spam" && (
                        <>
                          <button
                            onClick={() => handleApproveComment(comment.id)}
                            disabled={actionLoading}
                            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50"
                          >
                            <CheckCircle size={14} />
                            Approve
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        disabled={actionLoading}
                        className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>

                    {/* Replies Section */}
                    {comment.replies && comment.replies.length > 0 && (
                      <div className="mt-3">
                        <button
                          onClick={() => toggleReplies(comment.id)}
                          className="flex items-center gap-1 text-xs text-gray-500 hover:text-primary-600 transition-colors"
                        >
                          {expandedComments.has(comment.id) ? (
                            <ChevronDown size={14} />
                          ) : (
                            <ChevronRight size={14} />
                          )}
                          {comment.replies.length}{" "}
                          {comment.replies.length === 1 ? "reply" : "replies"}
                        </button>

                        {expandedComments.has(comment.id) && (
                          <div className="mt-3 pl-4 border-l-2 border-gray-200 dark:border-gray-700 space-y-3">
                            {comment.replies.map((reply) => {
                              const replyStatus =
                                reply.status?.toLowerCase() || "pending";
                              return (
                                <div
                                  key={reply.id}
                                  className="p-3 bg-gray-100/50 dark:bg-gray-800/30 rounded-lg"
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-400 to-secondary-400 flex items-center justify-center text-white text-xs font-medium">
                                        {reply.userName
                                          ?.charAt(0)
                                          .toUpperCase() || "U"}
                                      </div>
                                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                                        {reply.userName || "Anonymous"}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {formatTime(reply.createdAt)}
                                      </span>
                                      {getStatusBadge(reply.status)}
                                    </div>
                                  </div>
                                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    {reply.content}
                                  </p>
                                  <div className="flex gap-2">
                                    {replyStatus === "pending" && (
                                      <>
                                        <button
                                          onClick={() =>
                                            handleApproveComment(reply.id)
                                          }
                                          disabled={actionLoading}
                                          className="px-2 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                                        >
                                          Approve
                                        </button>
                                        <button
                                          onClick={() =>
                                            handleRejectComment(reply.id)
                                          }
                                          disabled={actionLoading}
                                          className="px-2 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                                        >
                                          Spam
                                        </button>
                                      </>
                                    )}
                                    {replyStatus === "approved" && (
                                      <button
                                        onClick={() =>
                                          handleRejectComment(reply.id)
                                        }
                                        disabled={actionLoading}
                                        className="px-2 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                                      >
                                        Mark as Spam
                                      </button>
                                    )}
                                    {replyStatus === "spam" && (
                                      <button
                                        onClick={() =>
                                          handleApproveComment(reply.id)
                                        }
                                        disabled={actionLoading}
                                        className="px-2 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                                      >
                                        Approve
                                      </button>
                                    )}
                                    <button
                                      onClick={() =>
                                        handleDeleteComment(reply.id)
                                      }
                                      disabled={actionLoading}
                                      className="px-2 py-1 bg-gray-600 text-white text-xs rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 p-5 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Showing {filteredComments.length} of {stats.total} comments
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium hover:opacity-90 transition-all"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Categories Management Modal
const CategoriesManagementModal = ({ onClose }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryImage, setCategoryImage] = useState(null);
  const [categoryImagePreview, setCategoryImagePreview] = useState("");
  const { success: toastSuccess, error: toastError } = useToast();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await blogService.getBlogCategories();

      let categoriesData = [];
      if (response?.data?.data?.categories) {
        categoriesData = response.data.data.categories;
      } else if (response?.data?.categories) {
        categoriesData = response.data.categories;
      } else if (response?.categories) {
        categoriesData = response.categories;
      } else if (Array.isArray(response?.data)) {
        categoriesData = response.data;
      } else if (Array.isArray(response)) {
        categoriesData = response;
      }

      setCategories(categoriesData);
    } catch (err) {
      setError("Failed to load categories");
      toastError("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCategoryImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCategoryImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toastError("Category name is required");
      return;
    }

    setActionLoading(true);
    try {
      const categoryData = {
        name: formData.name.trim(),
        description: formData.description || undefined,
      };

      if (editingCategory) {
        // updateBlogCategory expects (id, categoryData, image)
        await blogService.updateBlogCategory(
          editingCategory.id,
          categoryData,
          categoryImage, // Pass image file (null if unchanged)
        );
        toastSuccess("Category updated");
      } else {
        // createBlogCategory expects (categoryData, image)
        await blogService.createBlogCategory(
          categoryData,
          categoryImage, // Pass image file (null if none)
        );
        toastSuccess("Category created");
      }

      await fetchCategories();
      setShowAddForm(false);
      setEditingCategory(null);
      setFormData({ name: "", description: "" });
      setCategoryImage(null);
      setCategoryImagePreview("");
    } catch (err) {
      toastError(
        err.response?.data?.message || err.message || "Failed to save category",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (category) => {
    if (
      !window.confirm(
        `Delete category "${category.name}"? This will remove the category from all posts.`,
      )
    )
      return;

    setActionLoading(true);
    try {
      await blogService.deleteBlogCategory(category.id);
      toastSuccess("Category deleted");
      await fetchCategories();
    } catch (err) {
      toastError("Failed to delete category");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || "",
    });
    if (category.image) {
      setCategoryImagePreview(category.image);
    }
    setShowAddForm(true);
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-8">
          <Loader2
            size={40}
            className="animate-spin text-primary-500 mx-auto"
          />
          <p className="text-gray-500 mt-4">Loading categories...</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 p-5 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Manage Categories
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Organize your blog posts with categories
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowAddForm(!showAddForm);
                setEditingCategory(null);
                setFormData({ name: "", description: "" });
                setCategoryImage(null);
                setCategoryImagePreview("");
              }}
              className="px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl text-sm font-medium flex items-center gap-2"
            >
              <PlusCircle size={16} />
              Add Category
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center gap-3 text-red-600 text-sm">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          {showAddForm && (
            <div className="mb-6 p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                {editingCategory ? "Edit Category" : "New Category"}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={3}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingCategory(null);
                      setFormData({ name: "", description: "" });
                      setCategoryImage(null);
                      setCategoryImagePreview("");
                    }}
                    className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
                  >
                    {actionLoading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <SaveIcon size={16} />
                    )}
                    {editingCategory ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {categories.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen size={48} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">No categories yet</h3>
              <p className="text-gray-500">
                Click "Add Category" to create your first category
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="flex items-center justify-between p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex-1 flex items-center gap-3">
                    {category.image && (
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <FolderOpen size={16} className="text-primary-500" />
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {category.name}
                        </h3>
                        <span className="text-xs text-gray-500">
                          ({category.postsCount || 0} posts)
                        </span>
                      </div>
                      {category.description && (
                        <p className="text-sm text-gray-500 mt-1">
                          {category.description}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Slug: {category.slug}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(category)}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <Edit2 size={16} className="text-blue-500" />
                    </button>
                    <button
                      onClick={() => handleDelete(category)}
                      className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <Trash2 size={16} className="text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 p-5 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium hover:opacity-90 transition-all"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Delete Modal Component
const DeleteModal = ({ blog, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const { error: toastError } = useToast();

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      toastError(err.message || "Failed to delete blog");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-full">
              <AlertTriangle
                size={24}
                className="text-red-600 dark:text-red-400"
              />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Delete Blog Post
            </h2>
          </div>

          <p className="text-gray-600 dark:text-gray-400 mb-2">
            Are you sure you want to delete "{blog?.title}"?
          </p>
          <p className="text-sm text-red-600 dark:text-red-400 mb-6">
            This action cannot be undone. The blog will be permanently deleted.
          </p>

          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Trash2 size={16} />
              )}
              {loading ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Details Modal Component with Images
const DetailsModal = ({ blog, onClose, onEdit }) => {
  const status = getStatusInfo(blog.status);
  const StatusIcon = status.icon;
  const { success: toastSuccess } = useToast();
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);

  const handleCopyLink = () => {
    const url = `${window.location.origin}/blog/${blog.slug}`;
    navigator.clipboard.writeText(url);
    toastSuccess("Link copied to clipboard!");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 p-5 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${status.bg} ${status.color}`}>
              <StatusIcon size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Blog Details
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {status.label} • {formatTime(blog.createdAt)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Featured Image */}
          {blog.featuredImage && (
            <div className="mb-6">
              <img
                src={blog.featuredImage}
                alt={blog.title}
                className="w-full h-64 object-cover rounded-xl"
              />
            </div>
          )}

          {/* Statistics */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <BarChart3 size={18} /> Statistics
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl text-center">
                <Eye size={16} className="mx-auto mb-1 text-blue-500" />
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {blog.views || 0}
                </p>
                <p className="text-xs text-gray-500">Views</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl text-center">
                <ThumbsUpIcon2
                  size={16}
                  className="mx-auto mb-1 text-green-500"
                />
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {blog.likes || 0}
                </p>
                <p className="text-xs text-gray-500">Likes</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl text-center">
                <MessageCircle
                  size={16}
                  className="mx-auto mb-1 text-purple-500"
                />
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {blog.commentsCount || 0}
                </p>
                <p className="text-xs text-gray-500">Comments</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl text-center">
                <Share2Icon
                  size={16}
                  className="mx-auto mb-1 text-orange-500"
                />
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {blog.shares || 0}
                </p>
                <p className="text-xs text-gray-500">Shares</p>
              </div>
            </div>
          </div>

          {/* Title Section */}
          <div className="mb-6 mt-4">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              {blog.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
              <span className="flex items-center gap-1">
                <User size={14} /> {blog.author || "Unknown"}
              </span>
              <span className="flex items-center gap-1">
                <Calendar size={14} />{" "}
                {blog.publishedAt
                  ? format(new Date(blog.publishedAt), "PPP")
                  : format(new Date(blog.createdAt), "PPP")}
              </span>
              <span className="flex items-center gap-1">
                <Eye size={14} /> {blog.views || 0} views
              </span>
              <span className="flex items-center gap-1">
                <ThumbsUpIcon2 size={14} /> {blog.likes || 0} likes
              </span>
              <span className="flex items-center gap-1">
                <MessageCircle size={14} /> {blog.commentsCount || 0} comments
              </span>
              <span className="flex items-center gap-1">
                <Clock size={14} /> {blog.readingTime || "3 min read"}
              </span>
            </div>

            {/* Category */}
            {blog.category && (
              <div className="mb-4">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                  <FolderOpen size={14} />
                  Category: {blog.category.name}
                </span>
              </div>
            )}

            {/* Flags */}
            <div className="flex flex-wrap gap-2 mb-4">
              {blog.isFeatured && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400">
                  <Star size={12} /> Featured
                </span>
              )}
              {blog.isPopular && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400">
                  <ThumbsUpIcon2 size={12} /> Popular
                </span>
              )}
              {blog.isTrending && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400">
                  <TrendingUp size={12} /> Trending
                </span>
              )}
            </div>

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {blog.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                  >
                    <Tag size={10} /> {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Excerpt */}
            {blog.excerpt && (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-l-4 border-primary-500">
                <p className="text-gray-600 dark:text-gray-400 italic">
                  {blog.excerpt}
                </p>
              </div>
            )}
          </div>

          {/* Gallery Images */}
          {blog.galleryImages && blog.galleryImages.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <ImageIcon size={18} /> Gallery
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {blog.galleryImages.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Gallery ${index + 1}`}
                    className="h-32 w-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setSelectedGalleryImage(image)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <FileText size={18} /> Content
            </h3>
            <div
              className="prose prose-sm dark:prose-invert max-w-none prose-headings:font-semibold prose-a:text-primary-600 prose-img:rounded-lg prose-pre:bg-gray-800 prose-pre:text-gray-100"
              dangerouslySetInnerHTML={{
                __html: blog.content || "No content available",
              }}
            />
          </div>

          {/* SEO Information */}
          {(blog.metaTitle || blog.metaDescription || blog.metaKeywords) && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <GlobeIcon size={18} /> SEO Information
              </h3>
              <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                {blog.metaTitle && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Meta Title</p>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {blog.metaTitle}
                    </p>
                  </div>
                )}
                {blog.metaDescription && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      Meta Description
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {blog.metaDescription}
                    </p>
                  </div>
                )}
                {blog.metaKeywords && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Meta Keywords</p>
                    <div className="flex flex-wrap gap-2">
                      {blog.metaKeywords.split(",").map((keyword, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded-full"
                        >
                          {keyword.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 p-5 flex justify-between gap-3">
          <button
            onClick={handleCopyLink}
            className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2"
          >
            <LinkIcon size={16} />
            Copy Link
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium"
            >
              Close
            </button>
            <button
              onClick={() => onEdit(blog)}
              className="px-6 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium hover:opacity-90 transition-all flex items-center gap-2"
            >
              <Edit2 size={16} />
              Edit Blog
            </button>
          </div>
        </div>
      </motion.div>

      {/* Lightbox for gallery images */}
      {selectedGalleryImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={() => setSelectedGalleryImage(null)}
        >
          <img
            src={selectedGalleryImage}
            alt="Gallery full size"
            className="max-w-full max-h-full object-contain"
          />
          <button
            onClick={() => setSelectedGalleryImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <X size={24} className="text-white" />
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

// Main Component
const BlogManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [featuredFilter, setFeaturedFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedBlogs, setSelectedBlogs] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const { success: toastSuccess, error: toastError } = useToast();

  const debounceTimeout = useRef(null);

  // Debounce search term
  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [searchTerm]);

  // Fetch blogs when filters change (not on every keystroke)
  useEffect(() => {
    fetchBlogs();
  }, [
    statusFilter,
    featuredFilter,
    sortBy,
    sortOrder,
    currentPage,
    debouncedSearchTerm,
  ]);

  // Fetch stats only on mount
  useEffect(() => {
    fetchStats();
  }, []);

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        sortBy,
        sortOrder,
        ...(statusFilter !== "all" && { status: statusFilter.toLowerCase() }),
        ...(featuredFilter !== "all" && {
          isFeatured: featuredFilter === "featured",
        }),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
      };
      const response = await blogService.getAllBlogs(params);

      let blogsData = [];
      let paginationData = {};

      if (response?.data?.data?.blogs) {
        blogsData = response.data.data.blogs;
        paginationData = response.data.data.pagination;
      } else if (response?.data?.blogs) {
        blogsData = response.data.blogs;
        paginationData = response.data.pagination;
      } else if (response?.blogs) {
        blogsData = response.blogs;
        paginationData = response.pagination;
      } else if (Array.isArray(response?.data?.data)) {
        blogsData = response.data.data;
      } else if (Array.isArray(response?.data)) {
        blogsData = response.data;
      } else if (Array.isArray(response)) {
        blogsData = response;
      }

      setBlogs(blogsData || []);
      setTotalBlogs(paginationData?.total || blogsData.length);
      setTotalPages(paginationData?.pages || paginationData?.totalPages || 1);
    } catch (err) {
      toastError(err.message || "Failed to load blogs");
    } finally {
      setLoading(false);
    }
  }, [
    statusFilter,
    featuredFilter,
    sortBy,
    sortOrder,
    currentPage,
    debouncedSearchTerm,
  ]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await blogService.getBlogStats();

      let statsData = null;
      if (response?.data?.data) statsData = response.data.data;
      else if (response?.data) statsData = response.data;
      else if (response?.stats) statsData = response.stats;

      if (statsData) {
        setStats({
          total: statsData.total || 0,
          published: statsData.published || 0,
          drafts: statsData.drafts || 0,
          scheduled: statsData.scheduled || 0,
          archived: statsData.archived || 0,
          featured: statsData.featured || 0,
          totalViews: statsData.totalViews || 0,
          totalLikes: statsData.totalLikes || 0,
          totalComments: statsData.totalComments || 0,
        });
      } else {
        setStats({
          total: 0,
          published: 0,
          drafts: 0,
          scheduled: 0,
          archived: 0,
          featured: 0,
          totalViews: 0,
          totalLikes: 0,
          totalComments: 0,
        });
      }
    } catch (err) {
      setStats({
        total: 0,
        published: 0,
        drafts: 0,
        scheduled: 0,
        archived: 0,
        featured: 0,
        totalViews: 0,
        totalLikes: 0,
        totalComments: 0,
      });
    }
  }, []);

  const refreshAll = useCallback(() => {
    fetchBlogs();
    fetchStats();
  }, [fetchBlogs, fetchStats]);

  const handlePublish = async (id) => {
    try {
      await blogService.publishBlog(id);
      toastSuccess("Blog published successfully");
      refreshAll();
    } catch (err) {
      toastError("Failed to publish blog");
    }
  };

  const handleArchive = async (id) => {
    try {
      await blogService.archiveBlog(id);
      toastSuccess("Blog archived");
      refreshAll();
    } catch (err) {
      toastError("Failed to archive");
    }
  };

  const handleDelete = async (id) => {
    try {
      await blogService.deleteBlog(id);
      toastSuccess("Blog deleted");
      refreshAll();
      setSelectedBlogs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    } catch (err) {
      toastError("Failed to delete");
    }
  };

  const handleToggleFeatured = async (id, isFeatured) => {
    try {
      await blogService.toggleFeatured(id, isFeatured);
      toastSuccess(isFeatured ? "Blog featured" : "Blog unfeatured");
      refreshAll();
    } catch (err) {
      toastError("Failed to update featured status");
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedBlogs.size) return;
    if (!window.confirm(`Delete ${selectedBlogs.size} blog(s)?`)) return;
    try {
      await blogService.bulkDeleteBlogs(Array.from(selectedBlogs));
      toastSuccess(`${selectedBlogs.size} blog(s) deleted`);
      setSelectedBlogs(new Set());
      setSelectAll(false);
      refreshAll();
    } catch (err) {
      toastError("Failed to delete blogs");
    }
  };

  const handleBulkArchive = async () => {
    if (!selectedBlogs.size) return;
    try {
      await blogService.bulkArchiveBlogs(Array.from(selectedBlogs));
      toastSuccess(`${selectedBlogs.size} blog(s) archived`);
      setSelectedBlogs(new Set());
      setSelectAll(false);
      refreshAll();
    } catch (err) {
      toastError("Failed to archive blogs");
    }
  };

  const handleBulkPublish = async () => {
    if (!selectedBlogs.size) return;
    try {
      await blogService.bulkPublishBlogs(Array.from(selectedBlogs));
      toastSuccess(`${selectedBlogs.size} blog(s) published`);
      setSelectedBlogs(new Set());
      setSelectAll(false);
      refreshAll();
    } catch (err) {
      toastError("Failed to publish blogs");
    }
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    setSelectedBlogs(selectAll ? new Set() : new Set(blogs.map((b) => b.id)));
  };

  const handleSelectBlog = (id) => {
    const newSet = new Set(selectedBlogs);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    setSelectedBlogs(newSet);
    setSelectAll(newSet.size === blogs.length);
  };

  const statCards = [
    { icon: FileText, label: "Total Blogs", value: stats?.total, trend: 12 },
    { icon: CheckCircle, label: "Published", value: stats?.published },
    { icon: Eye, label: "Total Views", value: stats?.totalViews },
    { icon: ThumbsUpIcon2, label: "Total Likes", value: stats?.totalLikes },
    {
      icon: MessageCircle,
      label: "Total Comments",
      value: stats?.totalComments,
    },
  ];

  if (loading && !blogs.length) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2
            size={48}
            className="animate-spin text-primary-500 mx-auto mb-4"
          />
          <p className="text-gray-500">Loading blogs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-6 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      <div className="space-y-6 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 bg-clip-text text-transparent">
              Blog Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage blog posts, categories, and moderate comments
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowCategoriesModal(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-all"
            >
              <FolderOpen size={18} />
              Categories
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-all"
            >
              <PlusCircle size={18} />
              Create Blog
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {statCards.map((card, i) => (
            <StatCard
              key={i}
              icon={card.icon}
              label={card.label}
              value={card.value}
              trend={card.trend}
            />
          ))}
        </div>

        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search by title, content, author..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="scheduled">Scheduled</option>
              <option value="archived">Archived</option>
            </select>
            <select
              value={featuredFilter}
              onChange={(e) => setFeaturedFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Blogs</option>
              <option value="featured">Featured Only</option>
              <option value="not-featured">Not Featured</option>
            </select>
            <button
              onClick={refreshAll}
              className="px-4 py-2.5 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border rounded-xl flex items-center gap-2 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />{" "}
              Refresh
            </button>
          </div>
        </div>

        {selectedBlogs.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3"
          >
            <span className="text-sm font-semibold">
              {selectedBlogs.size} selected
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleBulkPublish}
                className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Publish
              </button>
              <button
                onClick={handleBulkArchive}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                Archive
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        )}

        {error && (
          <div className="p-4 bg-red-50 rounded-2xl flex items-center gap-3">
            <AlertCircle className="text-red-500" />
            <p className="text-red-600 text-sm">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto">
              <X size={16} />
            </button>
          </div>
        )}

        <div className="space-y-3">
          <AnimatePresence>
            {blogs.map((blog) => (
              <BlogCard
                key={blog.id}
                blog={blog}
                isSelected={selectedBlogs.has(blog.id)}
                onSelect={handleSelectBlog}
                onViewDetails={(b) => {
                  setSelectedBlog(b);
                  setShowDetailsModal(true);
                }}
                onManageComments={(b) => {
                  setSelectedBlog(b);
                  setShowCommentsModal(true);
                }}
                onEdit={(b) => {
                  setSelectedBlog(b);
                  setShowEditModal(true);
                }}
                onPublish={handlePublish}
                onArchive={handleArchive}
                onDelete={(b) => {
                  setSelectedBlog(b);
                  setShowDeleteModal(true);
                }}
                onToggleFeatured={handleToggleFeatured}
              />
            ))}
          </AnimatePresence>
        </div>

        {!blogs.length && !loading && (
          <div className="text-center py-12 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border">
            <FileText size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold mb-2">No blogs found</h3>
            <p className="text-gray-500">
              {searchTerm
                ? "Try adjusting your search"
                : "Click 'Create Blog' to get started"}
            </p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Prev
            </button>
            <span className="px-3 py-1.5">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showDetailsModal && selectedBlog && (
          <DetailsModal
            blog={selectedBlog}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedBlog(null);
            }}
            onEdit={(b) => {
              setShowDetailsModal(false);
              setSelectedBlog(b);
              setShowEditModal(true);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDeleteModal && selectedBlog && (
          <DeleteModal
            blog={selectedBlog}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedBlog(null);
            }}
            onConfirm={() => handleDelete(selectedBlog.id)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCreateModal && (
          <CreateEditBlogModal
            blog={null}
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              refreshAll();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEditModal && selectedBlog && (
          <CreateEditBlogModal
            blog={selectedBlog}
            onClose={() => {
              setShowEditModal(false);
              setSelectedBlog(null);
            }}
            onSuccess={() => {
              setShowEditModal(false);
              setSelectedBlog(null);
              refreshAll();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCommentsModal && selectedBlog && (
          <CommentsModerationModal
            blog={selectedBlog}
            onClose={() => {
              setShowCommentsModal(false);
              setSelectedBlog(null);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCategoriesModal && (
          <CategoriesManagementModal
            onClose={() => setShowCategoriesModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default BlogManagement;
